import { useState, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, RotateCcw, WifiOff } from "lucide-react";
import { Button } from "./Button";
import { classifyError, errorReference, type ErrorKind } from "@/lib/error-kind";
import { revenirSurLaBonneVoie } from "@/lib/reconnect";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  /** L'erreur brute (TanStack Query, `ApiError`…). Détermine le message affiché. */
  error?: unknown;
  /** Force la nature de l'erreur au lieu de la déduire de `error`. */
  kind?: ErrorKind;
  /** Relance la requête. Typiquement le `refetch` de TanStack Query. */
  onRetry?: () => void;
  /** Surcharges ponctuelles du texte par défaut. */
  title?: string;
  description?: ReactNode;
  className?: string;
}

interface Copy {
  title: string;
  description: ReactNode;
  icon: typeof AlertTriangle;
}

// Le texte du cas par défaut est celui validé par la cliente (tutoiement inclus). Les
// autres variantes existent pour ne pas annoncer un « souci de connexion » quand la vraie
// cause est ailleurs — c'était le principal défaut de l'ancien message unique.
const COPY: Record<ErrorKind, Copy> = {
  offline: {
    title: "Tu sembles hors ligne 📡",
    description: "Vérifie ta connexion internet, puis réessaie.",
    icon: WifiOff,
  },
  session: {
    title: "Ta session a expiré 🔐",
    description: "Reconnecte-toi pour continuer, tu retrouveras tout à sa place.",
    icon: AlertTriangle,
  },
  client: {
    title: "Cette page n'est pas accessible",
    description: "Le contenu demandé est introuvable ou ne t'est pas accessible.",
    icon: AlertTriangle,
  },
  server: {
    title: "Oups, petit souci de connexion 👀",
    description: "Pas d'inquiétude, ça se règle en un instant. Clique ci-dessous pour réessayer.",
    icon: AlertTriangle,
  },
  unknown: {
    title: "Oups, petit souci de connexion 👀",
    description: "Pas d'inquiétude, ça se règle en un instant. Clique ci-dessous pour réessayer.",
    icon: AlertTriangle,
  },
};

/** Affiché après un réessai infructueux : on escalade vers la reconnexion propre. */
const PERSISTENT_DESCRIPTION = (
  <>
    Pas d'inquiétude, ça se règle en un instant.
    <br />
    Clique ci-dessous pour te reconnecter.
    <br />
    <br />
    Si le souci persiste, ferme complètement l'onglet, puis rouvre la plateforme, tout devrait
    rentrer dans l'ordre 💙
  </>
);

export function ErrorState({
  error,
  kind,
  onRetry,
  title,
  description,
  className,
}: ErrorStateProps) {
  const [hasRetried, setHasRetried] = useState(false);

  const resolvedKind = kind ?? classifyError(error);
  const reference = errorReference(error);

  // Un simple refetch règle la majorité des cas sans rien faire perdre à l'utilisateur.
  // On ne propose la reconnexion (qui recharge la page) qu'après l'échec de ce réessai.
  // Hors ligne fait exception : recharger ne sert à rien tant que le réseau est coupé,
  // on laisse donc réessayer autant de fois que nécessaire.
  const canRetry =
    Boolean(onRetry) && resolvedKind !== "session" && (resolvedKind === "offline" || !hasRetried);
  // Un 4xx applicatif (droits, ressource supprimée) ne se règle généralement pas par une
  // reconnexion, mais un 4xx transitoire (course, état backend temporairement incohérent) si —
  // et surtout, ne jamais laisser l'utilisateur sans aucun recours après un réessai infructueux
  // (cas vécu : obligé de fermer complètement l'onglet pour revenir, cf. bilan client 28-29/07).
  const escalated = !canRetry && resolvedKind !== "offline";

  const copy = COPY[resolvedKind];
  const Icon = copy.icon;

  const resolvedDescription =
    description ??
    (escalated &&
    (resolvedKind === "server" || resolvedKind === "unknown" || resolvedKind === "client")
      ? PERSISTENT_DESCRIPTION
      : copy.description);

  return (
    <div className={cn("card-surface px-6 py-16 text-center", className)}>
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
        <Icon size={28} />
      </div>

      <h3 className="text-base font-black uppercase tracking-tight text-[#003366]">
        {title ?? copy.title}
      </h3>

      <p className="mx-auto mt-2 max-w-sm text-sm font-medium leading-relaxed text-gray-500">
        {resolvedDescription}
      </p>

      {(canRetry || escalated) && (
        <div className="mt-6">
          {canRetry ? (
            <Button
              variant="primary"
              icon={RefreshCw}
              onClick={() => {
                setHasRetried(true);
                onRetry?.();
              }}
            >
              Réessayer
            </Button>
          ) : (
            <Button variant="primary" icon={RotateCcw} onClick={revenirSurLaBonneVoie}>
              Revenir sur la bonne voie
            </Button>
          )}
        </div>
      )}

      {reference && (
        <p className="mt-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-300">
          Réf. {reference}
        </p>
      )}
    </div>
  );
}
